<script setup lang="ts">
import { h, render } from 'vue';

// maptiler sdk. @see https://docs.maptiler.com/sdk-js/
import * as maptilersdk from '@maptiler/sdk';
import debounce from 'debounce';

// icons
import { faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import CiMapMarker from '@/components/map/CiMapMarker/CiMapMarker.vue';
import CiMapMarkerCluster from '@/components/map/CiMapMarker/CiMapMarkerCluster.vue';
import CiMapCampsiteModal from '@/components/map/CiMapCampsiteModal/CiMapCampsiteModal.vue';

/********************
 * PROPS & EMITS    *
 ********************/
export interface CiMapProps {
  lat?: string | number;
  lng?: string | number;
  mapControls?: HTMLElement | null;
  preSelected?: object;
  query?: object;
  zoom?: number;
}
const props = withDefaults(defineProps<CiMapProps>(), {
  lat: 51.11,
  lng: 10.4,
  mapControls: null,
  preSelected: () => ({}),
  query: () => ({}),
  zoom: 5,
});

const emit = defineEmits<{
  'map-change-bounds': [value: { bbox: number[]; zoom: number }];
  'map-campsite-deselected': [];
}>();

defineExpose({
  disableChangeEvents,
  enableChangeEvents,
  getMapZoomLevel,
  openSelectedMarker,
  setMarkers,
});

/********************
 * COMPOSITIONS      *
 ********************/
const getRouteBaseName = useRouteBaseName();
const mapSettings = useMapSettings();
const nuxtApp = useNuxtApp();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const searchService = useSearchService();
const searchStore = useSearchStore();
const userStore = useUserStore();
const viewport = useViewport();
const { closeModal, openModal } = useCiModal();
const { getBoundsFromQueryString } = useMapFunctions();
const { locale } = useI18n();
const { setCustomUrl, trackStructEvent } = useTracking();

/********************
 * REFS & VARS      *
 ********************/
let map: maptilersdk.Map | null = null;
const campsiteRef = ref<HTMLElement | null>(null);
const changeEventsDisabled = ref<boolean>(false);
const disableNextEvent = ref<boolean>(false);
const isPending = ref<boolean>(false);
const mapIsReady = ref<boolean>(false);
const mapRef = ref<HTMLElement | null>(null);
const selected = ref({});
const selectedCampsiteDetails = ref(null);
const showPreview = ref<boolean>(false);

let markers: maptilersdk.Marker[] = [];

const isMobile = computed(() => {
  return viewport.isLessThan('md');
});

const isAsSearch = computed(() => {
  // @ts-check
  return !!(props.query.from || props.query.until);
});

/********************
 * FUNCTIONS         *
 ********************/

const debouncedOnChangeMap = debounce(onChangeMap, 250);

function initializeMap() {
  const useGL = !nuxtApp.$device.isOldMobileDevice && nuxtApp.$device.supportsWebGL;
  const settings = {
    language: `name:${locale.value}`,
    minZoom: mapSettings.MAP_MIN_ZOOM,
    maxZoom: mapSettings.MAP_MAX_ZOOM,
    zoomSnap: 1,
    worldCopyJump: true,
    center: { lon: props.lng, lat: props.lat },
    zoom: props.zoom,
    navigationControl: 'bottom-right',
    geolocateControl: 'bottom-right',
    terrainControl: 'bottom-right',
    maptilerLogo: false,
    renderWorldCopies: false,
    antialias: false,
    terrain: false,
  };

  if (mapRef.value) {
    if (useGL) {
      // @ts-check
      maptilersdk.config.apiKey = mapSettings.MAP_KEY;
      maptilersdk.config.session = true;
      // @ts-check
      map = new maptilersdk.Map({
        container: mapRef.value,
        style: maptilersdk.MapStyle.BASIC,
        ...settings,
      });
    } else {
      // @ts-check
      map = new maptilersdk.Map({
        container: mapRef.value,
        ...settings,
        style: {
          version: 8,
          sources: {
            'raster-tiles': {
              type: 'raster',
              tiles: [mapSettings.getMapTilesUrl(locale.value)],
              // @ts-check
              tileSize: mapSettings.MAP_TILEZIZE,
              attribution: mapSettings.MAP_ATTRIBUTION,
              crossOrigin: true,
            },
          },
          layers: [
            {
              id: 'ci-tiles',
              type: 'raster',
              source: 'raster-tiles',
              // @ts-check
              minzoom: mapSettings.MAP_MIN_ZOOM,
              // @ts-check
              maxzoom: mapSettings.MAP_MAX_ZOOM,
              // @ts-check
              crossOrigin: true,
            },
          ],
        },
      });
    }

    const bounds = getBoundsFromQueryString();
    if (bounds) {
      fitBounds(bounds, false, true);
    }

    addMapEvents();

    map.once('load', () => {
      mapIsReady.value = true;
      isPending.value = false;

      const q = route.query;
      const isMapPage = getRouteBaseName(route) === RBN_SEARCH_MAP;
      const queryHasMapParams = q.area || q.country || q.federal_state || q.city || q.region || q.q;
      if (!isMapPage || !queryHasMapParams) {
        onChangeMap();
      }
    });

    const url = getCurrentURL(runtimeConfig.public.baseUrl, route);
    setCustomUrl(url);
    trackStructEvent({
      category: 'map',
      action: 'initialize',
      property: 'gl',
      value: useGL ? 1.0 : 0.0,
    });
  }
}

function fitBounds(bounds: maptilersdk.LngLatBounds | null, usePadding: boolean = true, initial: boolean = false) {
  if (!bounds) {
    return;
  }
  try {
    let padding = 0;
    let additionalTopPadding = 0;
    if (usePadding) {
      padding = 50;
      if (props.mapControls) {
        additionalTopPadding = props.mapControls.offsetTop + props.mapControls.clientHeight;
      }
    }
    if (map) {
      map.fitBounds(bounds, {
        padding: {
          top: padding + additionalTopPadding,
          bottom: padding,
          left: padding,
          right: padding,
        },
        duration: initial ? 0 : 400,
      });
    }
    getAndSaveMapBounds(bounds);
  } catch (_e) {
    // _e
  }
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function round(value: number, precision: number = 2, func: Function = Math.round) {
  return func(value * 10 ** precision) / 10 ** precision;
}

function getMapZoomLevel() {
  if (!map) {
    return 4;
  }
  try {
    return Math.ceil(map.getZoom());
  } catch (_e) {
    return 4;
  }
}

function getAndSaveMapBounds(bounds: maptilersdk.LngLatBounds) {
  const precision = 6;
  const mapBounds = [
    round(bounds.getNorthWest().lng, precision, Math.ceil),
    round(bounds.getNorthWest().lat, precision, Math.floor),
    round(bounds.getSouthEast().lng, precision, Math.floor),
    round(bounds.getSouthEast().lat, precision, Math.ceil),
  ];
  searchStore.setMapSettings({ bounds: mapBounds, zl: getMapZoomLevel() });
  return mapBounds;
}

function addMapEvents() {
  if (!map) {
    return;
  }
  removeMapEvents();
  try {
    map.on('movestart', onChangeMapStart);
    map.on('move', debouncedOnChangeMap);
    map.on('click', closePreview);
  } catch (_e) {
    // _e
  }
}

function removeMapEvents() {
  if (!map || !map.off) {
    return;
  }
  try {
    map.off('movestart', onChangeMapStart);
    map.off('move', debouncedOnChangeMap);
    map.off('click', closePreview);
  } catch (_e) {
    // _e
  }
}

function onChangeMapStart() {
  // Event on map drag start
  closePreview();
}

function closePreview() {
  selectedCampsiteDetails.value = null;
  showPreview.value = false;
  selected.value = {};
  removeActiveMarker();

  if (campsiteRef.value) {
    campsiteRef.value.hideFlexAvailabilities();
  }

  emit('map-campsite-deselected');

  // remove campsite param from url
  const query = { ...route.query };
  if (query.campsite) {
    delete query.campsite;
    return navigateTo({ name: route.name || undefined, query }, { replace: true });
  }
}

function onChangeMap() {
  if (!map) {
    return;
  }
  isPending.value = true;

  // get bound values and trigger related event
  let bounds;
  let mapBounds = null;
  try {
    bounds = map.getBounds();
  } catch (_e) {
    return;
  }

  if (bounds) {
    mapBounds = getAndSaveMapBounds(bounds);
  }

  if (changeEventsDisabled.value) {
    return;
  }

  // Prevent event trigger, e.g. for inital setBounds
  if (disableNextEvent.value) {
    disableNextEvent.value = false;
    isPending.value = false;
    return;
  }

  if (mapBounds) {
    emit('map-change-bounds', {
      bbox: mapBounds,
      zoom: getMapZoomLevel(),
    });
  }
}

function setMarkers(campsites: any[], callZoomToMarker: boolean = false, locationBounds: any = null) {
  if (!map) {
    nextTick(() => {
      setMarkers(campsites, callZoomToMarker, locationBounds);
    });
    return;
  }

  if (props.preSelected && Object.keys(props.preSelected).length) {
    selected.value = props.preSelected;
  }

  const initial: boolean = markers.length === 0;

  removeAllMarkers();

  let campsitesArray = [];

  if (campsites !== null) {
    campsitesArray = campsites.sort((i1, i2) => (i1.rating_avg_overall || 0) - (i2.rating_avg_overall || 0));
  }

  // sort for the worst avg rating to use
  // that in the delayed animation inside the map marker
  const filteredCampsites = campsitesArray.filter(item => item.rating_avg_overall);
  // default (worst rating) value
  let worstRating = 1.0;

  if (filteredCampsites.length > 0) {
    worstRating = filteredCampsites[0].rating_avg_overall;
  }

  // Create map markers based on geo points
  markers = campsitesArray.map((campsite) => {
    let anchor: string = 'center';

    const el = document.createElement('div');
    el.style.cursor = 'pointer';

    // Condition: point is a single campsite
    if (campsite.slug) {
      anchor = 'bottom';
      const vNode = h(CiMapMarker, {
        isFavorite: cpIsFavorite(campsite.slug),
        campsite,
        clusterAvailableCount: campsite.available || 0,
        isAsSearch: isAsSearch.value,
        showEffect: props.preSelected && props.preSelected.slug === campsite.slug && props.preSelected.showEffect,
        worstRating,
      });
      render(vNode, el);
    } else {
      if (campsite.location_name) {
        anchor = 'top-left';
      }

      // see: https://vuejs.org/guide/extras/render-function#creating-vnodes
      const vNode = h(CiMapMarkerCluster, {
        clusterAvailableCount: campsite.available || 0,
        clusterItemCount: campsite.count,
        clusterItemLabel: campsite.location_name,
        isAsSearch: isAsSearch.value,
        locale: locale.value,
      });
      render(vNode, el);
    }

    const markerContainer = el.getElementsByClassName('map-marker__container')[0];

    if (selected.value && selected.value.slug === campsite.slug) {
      markerContainer.classList.add('active');
    }

    if (campsite.slug === props.preSelected.slug) {
      el.classList.add('z-10');
    }

    const marker: maptilersdk.Marker = new maptilersdk.Marker({
      element: el,
      // @ts-check
      anchor,
    });
    marker.setLngLat([campsite.geo_position.longitude, campsite.geo_position.latitude]);
    marker.addTo(map as maptilersdk.Map);
    marker.campsite = campsite;

    el.addEventListener('click', selectMarkerIcon);
    el.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      clickMarkerItem(campsite);
    });
    return marker;
  });

  if (callZoomToMarker) {
    // Disable mapchange event on setViewBounds
    zoomToMarker(true, locationBounds, initial);
    nextTick(() => {
      isPending.value = false;
    });
  } else {
    isPending.value = false;
  }
}

function removeAllMarkers() {
  markers.forEach((marker) => {
    marker.remove?.();
  });
  markers = [];
}

function removeActiveMarker() {
  if (!mapRef.value) {
    return;
  }
  // Deactivate so far active marker
  const activeMarker = mapRef.value.getElementsByClassName('map-marker__container active');
  if (activeMarker && activeMarker.length > 0) {
    activeMarker[0].classList.remove('active');
  }
}

function selectMarkerIcon(event: MouseEvent) {
  // Deactivate so far active marker
  removeActiveMarker();
  const closestMarkerContainer = event.target?.closest('.map-marker__container');
  if (closestMarkerContainer) {
    setTimeout(() => {
      closestMarkerContainer.classList.add('active');
    }, 10);
  }
}

function clickMarkerItem(data: any) {
  // is location
  if (data.location_slug && data.location_type) {
    const query = { ...route.query };

    // remove params
    const removeParams = ['country', 'region', 'federal_state', 'city', 'q', 'area'];
    Object.keys(query)
      .filter(k => removeParams.includes(k))
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      .forEach(q => delete query[q]);

    if (data.country_slug) {
      query.country = data.country_slug;
    }

    if (data.location_slug) {
      query[data.location_type] = data.location_slug;
    }

    return navigateTo({ query });
  }

  // is cluster
  if (data.count > 1 || data.location_name) {
    // const oldSnap = this.map.options.zoomSnap;
    // this.map.options.zoomSnap = 0.01;
    fitBounds(convertBoundsToLeaflet(data.bbox), true);
    // setTimeout(() => {
    //   this.map.options.zoomSnap = oldSnap;
    // }, 200);
    return;
  }

  openSelectedMarker(data);
}

function zoomToMarker(doDisableNextEvent = false, locationBounds = null, initial: boolean = false) {
  disableNextEvent.value = doDisableNextEvent;

  if (locationBounds) {
    fitBounds(convertBoundsToLeaflet(locationBounds), true, initial);
  } else if (markers.length === 1 && markers[0].campsite.slug === null) {
    const { bbox } = markers[0].campsite;
    fitBounds(convertBoundsToLeaflet(bbox), true, initial);
  } else {
    fitBounds(getOverallBounds(), true, initial);
  }

  disableNextEvent.value = false;
}

function getOverallBounds(): maptilersdk.LngLatBounds | null {
  let bounds: maptilersdk.LngLatBounds | null = null;

  markers.forEach((marker) => {
    let markerBounds: maptilersdk.LngLatBounds;

    if (marker.campsite.bbox) {
      markerBounds = convertBoundsToLeaflet(marker.campsite.bbox);
    } else {
      const geo = marker.campsite.geo_position;
      const lnglat = new maptilersdk.LngLat(geo.longitude, geo.latitude);
      markerBounds = new maptilersdk.LngLatBounds(lnglat, lnglat);
    }

    if (bounds) {
      bounds = bounds.extend(markerBounds);
    } else {
      bounds = markerBounds;
    }
  });

  return bounds;
}

function convertBoundsToLeaflet(inBounds: number[]): maptilersdk.LngLatBounds {
  let tlLat = inBounds[1];
  let tlLon = inBounds[0];
  let brLat = inBounds[3];
  let brLon = inBounds[2];

  [tlLat, tlLon] = normalizLatitudeLongitude(tlLat, tlLon);
  [brLat, brLon] = normalizLatitudeLongitude(brLat, brLon);

  return new maptilersdk.LngLatBounds([tlLon, tlLat], [brLon, brLat]);
}

function fmod(a: number, b: number) {
  return Number((a - Math.floor(a / b) * b).toPrecision(8));
}

function normalizeLongitude(lon: number): number {
  lon = fmod(lon, 360.0);
  if (lon > 180.0) {
    lon -= 360.0;
  } else if (lon <= -180.0) {
    lon += 360.0;
  }
  return lon;
}

function normalizLatitudeLongitude(lat: number, lon: number): number[] {
  lat = normalizeLongitude(lat);
  let flip = false;
  if (lat > 90.0) {
    lat = 180.0 - lat;
    flip = true;
  } else if (lat < -90.0) {
    lat = -180.0 - lat;
    flip = true;
  }
  if (flip) {
    lon += lon > 0 ? -180.0 : 180.0;
  }
  lon = normalizeLongitude(lon);
  return [lat, lon];
}

function disableChangeEvents() {
  changeEventsDisabled.value = true;
  isPending.value = true;
}

function enableChangeEvents() {
  changeEventsDisabled.value = false;
  isPending.value = false;
}

async function openSelectedMarker(campsite: any) {
  selected.value = campsite;
  isPending.value = true;

  selectedCampsiteDetails.value = await searchService.getMapDetail(selected.value.slug, locale.value, route.query);
  isPending.value = false;

  showPreview.value = true;

  if (getRouteBaseName(route) === RBN_SEARCH_MAP && selected.value.slug !== route.query.campsite) {
    return navigateTo(
      { name: route.name || undefined, query: { ...route.query, campsite: selected.value.slug } },
      { replace: true },
    );
  }
}

function showCampsiteModal() {
  openModal(
    {
      component: CiMapCampsiteModal,
      attrs: {
        campsite: selectedCampsiteDetails.value,
        getAvailabilities: async (slug: string) => {
          const response = await searchStore.getAvailabilities({
            slug,
            lang: locale.value,
            query: searchStore.searchParamsWithoutNulls,
          });
          return response?.flex || [];
        },
        name: 'CiMapCampsiteModal',
        onClose: () => {
          closeModal('CiMapCampsiteModal');
        },
      },
    },
    {
      clickToClose: true,
      contentTransition: 'translate-y-up',
      contentClass: 'p-2 w-full bottom-0 absolute max-w-[825px]',
    },
  );
}

function getFlexAvailabilitiesForCp(slug: string) {
  if (!searchStore.flexAvailabilities[slug]) {
    return null;
  }
  return searchStore.flexAvailabilities[slug].flex || null;
}

function onShowFlexClicked(campsite: any) {
  searchStore.getAvailabilities({
    slug: campsite.slug,
    lang: locale.value,
    query: searchStore.searchParamsWithoutNulls,
  });
}

function cpIsFavorite(cpSlug: string) {
  if (!userStore.loggedIn) {
    return false;
  }
  return userStore.favorites.includes(cpSlug);
}

/********************
 * HOOKS             *
 ********************/
onMounted(() => {
  nextTick(initializeMap);
});

onBeforeUnmount(() => {
  removeMapEvents();
  if (mapRef.value && mapRef.value.remove) {
    try {
      mapRef.value.remove();
    } catch (_e) {
      // _e
    }
  }
  mapRef.value = null;
});
</script>

<template>
  <div
    ref="mapRef"
    class="absolute left-0 top-0 z-0 size-full overflow-hidden font-sans"
  />

  <!-- campsite tile -->
  <div
    class="absolute left-1/2 z-20 flex h-auto w-full max-w-full -translate-x-1/2 rounded-lg pt-[23px] duration-200 ease-out md:max-h-[80%] md:max-w-[51.5625rem] md:bg-transparent"
    :class="[showPreview ? 'bottom-0 translate-y-0 md:bottom-5' : 'bottom-0 translate-y-full']"
  >
    <!-- close cp -->
    <button
      type="button"
      class="button absolute right-4 top-[-3px] rounded-lg rounded-b-none bg-white py-1 text-sm"
      @click="closePreview()"
    >
      <span>{{ $gettext('Close') }}</span>
      <CiAwesomeIcon
        :icon="faTimesCircle"
        ratio="0.6"
      />
    </button>

    <!-- mobile view cp -->
    <div
      v-if="isMobile && selectedCampsiteDetails"
      class="w-full bg-white"
      :class="{ 'min-h-[200px]': isPending }"
    >
      <CiMapCampsiteTileMobile
        v-if="!isPending"
        :loading="isPending"
        :snowplow-context="{
          bannerId: 'map',
          position: 1,
        }"
        :campsite="selectedCampsiteDetails"
        @open-details="showCampsiteModal"
      />
      <!-- loader -->
      <div
        v-else
        class="relative h-full"
      >
        <CiAnimatedLoadingIndicator class="absolute left-1/2 top-1/2 size-20 -translate-x-1/2 -translate-y-1/2" />
      </div>
    </div>

    <!-- desktop view cp -->
    <CiCampsiteTileAs
      v-if="!isMobile && selectedCampsiteDetails"
      ref="campsiteRef"
      class="ci-map__campsite-tile w-full overflow-auto rounded-lg"
      :loading="isPending"
      :snowplow-context="{
        bannerId: 'map',
        position: null,
      }"
      :campsite="selectedCampsiteDetails"
      :show-no-availability-info="true"
      :show-location-link="false"
      :allow-sponsored="false"
      :flex-availabilities="getFlexAvailabilitiesForCp(selectedCampsiteDetails.slug)"
      @show-flex="onShowFlexClicked(selectedCampsiteDetails)"
    />
  </div>
</template>

<style>
@import '@maptiler/sdk/dist/maptiler-sdk.css';
</style>

<style lang="scss">
.maplibregl-map .map-marker__container {
  font: initial;
  @apply text-base font-sans;
}

.page--search-map,
.ci-map__modal {
  .ci-map__campsite-tile {
    .campsite-tile {
      border: 0;
    }
  }
}
</style>
